import { useState } from 'react'
import type { ThemeFileVersionSummary, ThemeWorkspaceGrant } from '@fitting-room/contracts'
import { Check, Copy, KeyRound, Paintbrush } from 'lucide-react'
import { PageHeader } from '../../components/admin/PageHeader'
import { useOpenThemeWorkspace, useThemeHistory, useThemeWorkspace } from '../../lib/adminQueries'
import { useStaffToken } from '../../lib/staffToken'

/**
 * The theme workspace: hand an AI assistant a thirty-minute key to the
 * website's look.
 *
 * The screen does three things and nothing else. It mints the key and shows
 * it ONCE, as a block of text to paste into an assistant. It says, in words,
 * what that key can and cannot do - because the person holding it will be
 * asked "is this safe?" and should be able to answer. And it lists what the
 * assistant has written, as it writes it, so the owner watches the work
 * arrive rather than trusting a summary.
 *
 * What it deliberately does not have is a file editor. The assistant is the
 * editor; this page is where the key comes from and where the record lives.
 */

function CopyButton({ text, label }: { readonly text: string; readonly label: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      className="st-btn-ghost"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setCopied(true)
          window.setTimeout(() => setCopied(false), 2000)
        })
      }}
    >
      {copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
      {copied ? 'Copied' : label}
    </button>
  )
}

/**
 * The one block a person pastes - into Codex, Claude, a phone's chat box,
 * or a colleague's message. Written for a person first and an assistant
 * second, in that order on the page too: what this is, what it can do, how
 * long it lasts and where to get another, and only then the connection
 * details. A block that opens with a config stanza is unreadable on a phone
 * and unexplainable to anyone asked "what is this?".
 */
function pasteBlock(grant: ThemeWorkspaceGrant): string {
  const until = new Date(grant.expiresAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  const minutes = Math.round(grant.expiresInSeconds / 60)
  const renewAt = `${window.location.origin}/admin/theme`
  return [
    `This is a key to the gown pages of the Velvet Hour website.`,
    ``,
    `WHAT IT IS`,
    `An AI assistant can use it to change how the gown pages look, on a draft copy of the site: the browse page (the grid of gowns, its filters and sorting) and each gown's own page. It cannot touch anything else on the site, and nothing it does goes live - a person publishes the draft in Shopify after looking at it.`,
    ``,
    `WHAT YOU CAN ASK FOR`,
    `Layout (columns, spacing, what shows under each photo), wording, colors, the filters, the order gowns appear in, what a gown page shows and where the buttons go. Say it the way you would to a designer. After every change the assistant gives you a preview link; if you don't like it, say so and it puts the previous version back.`,
    ``,
    `THE KEY`,
    `It works for ${minutes} minutes, until ${until}. When it stops working, get a new one at ${renewAt} (owner sign-in). Everything already written stays.`,
    ``,
    `PREVIEW`,
    grant.theme.previewUrl ?? '(no preview yet)',
    ``,
    `FOR THE ASSISTANT`,
    `Connect to this MCP server, run its getting_started prompt, then ask me what I would like to change.`,
    `  Name: bridal-theme`,
    `  URL: ${grant.mcpUrl}`,
    `  Header: Authorization: Bearer ${grant.token}`,
  ].join('\n')
}

/** The block, once, with a Copy button. */
function GrantPanel({ grant }: { readonly grant: ThemeWorkspaceGrant }) {
  const minutes = Math.round(grant.expiresInSeconds / 60)
  const block = pasteBlock(grant)

  return (
    <div className="st-ok mb-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 font-medium">
          <KeyRound size={15} aria-hidden />
          Workspace open for {minutes} minutes
        </p>
        <CopyButton text={block} label="Copy everything" />
      </div>
      <p className="mt-1">
        Shown once. Copy the whole block and paste it into the assistant - Codex, Claude, on a phone or a computer. It
        reads as a note to a person first, and the assistant finds what it needs at the end.
      </p>
      <pre className="st-card mt-3 overflow-x-auto whitespace-pre-wrap break-words p-3 text-xs leading-relaxed">{block}</pre>
    </div>
  )
}

function when(iso: string): string {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function HistoryTable({ versions }: { readonly versions: readonly ThemeFileVersionSummary[] }) {
  if (versions.length === 0) {
    return <p className="st-meta">Nothing has been written through a workspace yet.</p>
  }
  return (
    <div className="st-card overflow-x-auto">
      <table className="st-table">
        <thead>
          <tr>
            <th>When</th>
            <th>File</th>
            <th>What changed</th>
            <th>By</th>
            <th>Size</th>
          </tr>
        </thead>
        <tbody>
          {versions.map((version) => (
            <tr key={version.id}>
              <td className="whitespace-nowrap tabular-nums">{when(version.createdAt)}</td>
              <td className="font-mono text-xs">{version.path}</td>
              <td>{version.note}</td>
              <td className="st-meta">{version.author}</td>
              <td className="st-meta tabular-nums">{(version.byteSize / 1024).toFixed(1)} KB</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function ThemePage() {
  const { token } = useStaffToken()
  const status = useThemeWorkspace(token)
  const open = useOpenThemeWorkspace(token)
  const [grant, setGrant] = useState<ThemeWorkspaceGrant | null>(null)
  const watching = grant !== null && Date.parse(grant.expiresAt) > Date.now()
  const history = useThemeHistory(token, watching)

  if (status.data && !status.data.configured) {
    return (
      <div>
        <PageHeader eyebrow="The boutique's website" title="Theme" />
        <div className="st-card px-4 py-10 text-center">
          <Paintbrush size={20} aria-hidden className="mx-auto mb-2 opacity-40" />
          <p className="st-meta">This boutique has no theme connected, so there is nothing for a workspace to edit.</p>
          <p className="st-meta mt-1">Publishing, the catalog and everything else on this console work exactly as they do.</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <PageHeader eyebrow="The boutique's website" title="Theme">
        <button type="button" className="st-btn" disabled={open.isPending || !status.data} onClick={() => open.mutate(undefined, { onSuccess: setGrant })}>
          <KeyRound size={14} aria-hidden />
          {open.isPending ? 'Opening...' : grant ? 'Open another' : 'Open a workspace'}
        </button>
      </PageHeader>

      <p className="st-meta mb-5 max-w-2xl">
        A workspace is a thirty-minute key you paste into an AI assistant. With it, the assistant can rewrite the gown
        pages on a draft copy of the theme - the files below, and new ones it makes in the same family - and nothing
        else: not the header, not the shoes, and never the live site. You look at the draft, and publish it in
        Shopify when it is right.
      </p>

      {open.isError ? (
        <div className="st-alert mb-5">
          <p>{open.error.message}</p>
          <p className="mt-1 text-xs opacity-70">If this keeps happening, please contact your system developer.</p>
        </div>
      ) : null}

      {grant ? <GrantPanel grant={grant} /> : null}

      {status.data ? (
        <div className="mb-6">
          <h2 className="st-display mb-2 text-base">What a workspace can touch</h2>
          <div className="st-card overflow-x-auto">
            <table className="st-table">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Last written</th>
                </tr>
              </thead>
              <tbody>
                {status.data.files.map((file) => (
                  <tr key={file.path}>
                    <td className="font-mono text-xs">{file.path}</td>
                    <td className="st-meta">
                      {file.latest ? `${when(file.latest.createdAt)} — ${file.latest.note}` : 'never, through a workspace'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="st-meta mt-2">
            New files it creates are named <span className="font-mono text-xs">{status.data.newFilePrefix}…</span> and
            appear here once written. Draft theme: <strong>{status.data.themeName}</strong>
            {status.data.previewUrl ? (
              <>
                {' '}
                ·{' '}
                <a className="underline" href={status.data.previewUrl} target="_blank" rel="noreferrer">
                  preview
                </a>
              </>
            ) : null}
          </p>
          {status.data.draft !== 'ready' ? (
            /*
             * The day after a publish. The draft became the site - which is
             * the point - and the next draft is a copy somebody makes in the
             * Shopify admin. Said here, with the steps, because the person
             * who published is the person reading this.
             */
            <div className="st-notice mt-3">
              <p className="font-medium">
                {status.data.draft === 'live'
                  ? 'The draft was published - it is the live site now.'
                  : status.data.draft === 'missing'
                    ? 'There is no draft theme on the shop yet.'
                    : 'The shop could not be reached.'}
              </p>
              {status.data.draft === 'live' || status.data.draft === 'missing' ? (
                <ol className="mt-1 list-decimal space-y-1 pl-5 text-sm">
                  <li>In Shopify: Online Store → Themes.</li>
                  {status.data.draft === 'live' ? (
                    <li>
                      On the live theme, Actions → Rename, to anything but <strong>{status.data.themeName}</strong>.
                    </li>
                  ) : null}
                  <li>
                    On the live theme, Actions → Duplicate, then rename the copy to <strong>{status.data.themeName}</strong>.
                  </li>
                  <li>Come back here and open a workspace.</li>
                </ol>
              ) : (
                <p className="mt-1 text-sm">{status.data.detail}</p>
              )}
            </div>
          ) : null}
        </div>
      ) : null}

      <h2 className="st-display mb-2 text-base">What has been written</h2>
      {history.data ? <HistoryTable versions={history.data.versions} /> : null}
    </div>
  )
}
